import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import {
  ColumnFilterCheckboxListPanel,
  setColumnFilterAllValues,
  setColumnFilterOneValue,
} from './columnFilterCheckboxList.jsx';
import { FILTER_POPOVER_CLEAR_BTN_STYLE } from './columnFilterPopoverStyles.js';

/**
 * Text-column filter popover: search field + checkbox list on unique column values.
 * Wired from AnnotationTablePage when the column is text/textarea (not message / email).
 * Updates the same `columnFilters[columnKey]` entry that filteredAnnotations reads.
 */
export function TextColumnWithCheckboxListFilter({
  columnKey,
  columnFilters,
  setColumnFilters,
  uniqueColumnValues,
}) {
  const filterEntry = columnFilters[columnKey];
  const searchText = filterEntry?.type === 'text' ? (filterEntry.value || '') : '';
  const allowedValues =
    filterEntry?.allowedValues || filterEntry?.valueSet || new Set();
  const excludesAllValues = !!(
    filterEntry?.excludeAllColumnValues || filterEntry?.emptyDiscrete
  );
  const hasPartialValueList =
    allowedValues.size > 0 && allowedValues.size < uniqueColumnValues.length;
  const checkboxListAllChecked =
    !excludesAllValues &&
    (!hasPartialValueList ||
      uniqueColumnValues.every((v) => allowedValues.has(String(v))));
  const checkboxListIndeterminate = !excludesAllValues && hasPartialValueList;

  return (
    <Box>
      <TextField
        size="small"
        placeholder="Search in this column..."
        fullWidth
        sx={{ mb: 2 }}
        value={searchText}
        onChange={(e) => {
          const typed = e.target.value;
          setColumnFilters((prev) => {
            const cur = prev[columnKey];
            const keptAllowed =
              cur?.type === 'text'
                ? new Set(cur.allowedValues || cur.valueSet || [])
                : null;
            const hadExcludedAll =
              cur?.type === 'text' &&
              (cur.excludeAllColumnValues || cur.emptyDiscrete);
            if (!typed.trim() && !keptAllowed?.size) {
              if (hadExcludedAll) {
                return {
                  ...prev,
                  [columnKey]: {
                    type: 'text',
                    value: '',
                    excludeAllColumnValues: true,
                  },
                };
              }
              const next = { ...prev };
              delete next[columnKey];
              return next;
            }
            const nextFilter = {
              type: 'text',
              value: typed,
              excludeAllColumnValues: false,
            };
            if (keptAllowed?.size > 0) {
              nextFilter.allowedValues = keptAllowed;
            }
            return { ...prev, [columnKey]: nextFilter };
          });
        }}
      />
      {searchText.trim() && (
        <button
          type="button"
          onClick={() => {
            setColumnFilters((prev) => {
              const cur = prev[columnKey];
              const keptAllowed =
                cur?.type === 'text'
                  ? new Set(cur.allowedValues || cur.valueSet || [])
                  : null;
              const hadExcludedAll =
                cur?.type === 'text' &&
                (cur.excludeAllColumnValues || cur.emptyDiscrete);
              if (!keptAllowed?.size && !hadExcludedAll) {
                const next = { ...prev };
                delete next[columnKey];
                return next;
              }
              const nextFilter = {
                type: 'text',
                value: '',
                excludeAllColumnValues: false,
              };
              if (keptAllowed?.size > 0) nextFilter.allowedValues = keptAllowed;
              if (hadExcludedAll) nextFilter.excludeAllColumnValues = true;
              return { ...prev, [columnKey]: nextFilter };
            });
          }}
          style={{
            marginBottom: '16px',
            ...FILTER_POPOVER_CLEAR_BTN_STYLE,
          }}
        >
          Clear search
        </button>
      )}

      <ColumnFilterCheckboxListPanel
        uniqueColumnValues={uniqueColumnValues}
        allChecked={checkboxListAllChecked}
        indeterminate={checkboxListIndeterminate}
        onAllChange={(e) =>
          setColumnFilters((prev) =>
            setColumnFilterAllValues(prev, columnKey, 'text', e.target.checked)
          )
        }
        getIsChecked={(value) =>
          excludesAllValues
            ? false
            : hasPartialValueList || allowedValues.size > 0
              ? allowedValues.has(String(value))
              : true
        }
        onRowToggle={(value, checked) =>
          setColumnFilters((prev) =>
            setColumnFilterOneValue(prev, columnKey, 'text', value, checked, uniqueColumnValues)
          )
        }
      />
      {(filterEntry?.type === 'text' &&
        ((searchText && searchText.trim()) ||
          allowedValues.size > 0 ||
          excludesAllValues)) && (
        <button
          type="button"
          onClick={() => {
            setColumnFilters((prev) => {
              const next = { ...prev };
              delete next[columnKey];
              return next;
            });
          }}
          style={{ marginTop: '8px', ...FILTER_POPOVER_CLEAR_BTN_STYLE }}
        >
          Clear filter
        </button>
      )}
    </Box>
  );
}
